import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AppShell } from '@/components/AppShell';
import { FunnelSteps, RankBars, RateBars } from '@/components/charts';
import { Notice, Panel } from '@/components/Panel';
import { FLAG_FLOOR_PCT, RUN_WINDOW, useLensData } from '@/data/source';
import { useAppState, useSettings } from '@/data/settings';
import type { EventRow } from '@/lens';
import {
  lensFlagNotice,
  lensFlagRates,
  lensFunnelNotice,
  lensRunSummariesV2,
  lensSceneFunnel,
  lensSceneStats,
  lensSceneVerbDist,
  lensSoroSceneUrl,
  lensSourceBadge,
  lensTurnProblemSummary,
  lensTurnTimelineV2,
} from '@/lens';

/**
 * 탐색 — 회차/장면 목록과 상세.
 *
 * ⚠️⚠️ **엔티티 화면(상세)에는 기간 필터를 걸지 않고 칩도 숨긴다.**
 * 「오늘」을 고른 채 지난주 회차를 열면 타임라인이 통째로 비어 **고장처럼
 * 보인다.** ★ 원본도 상세에서는 `lensFilterByRange` 를 아예 부르지 않는다.
 */


const AXIS = {
  runs: 'var(--lens-axis-runs)',
  scenes: 'var(--lens-axis-scenes)',
} as const;

const TONE_BG: Record<string, { bg: string; fg: string }> = {
  good: { bg: 'var(--lens-accent-success)', fg: 'var(--lens-success)' },
  warn: { bg: 'var(--lens-accent-warning)', fg: 'var(--lens-warning)' },
  bad: { bg: 'var(--lens-accent-error)', fg: 'var(--lens-error)' },
};

function Chip({ tone, children }: { tone: string; children: React.ReactNode }) {
  const c = TONE_BG[tone];
  if (c === undefined) return <span className="text-label-md">{children}</span>;
  return (
    <span
      className="rounded-pill px-sm py-xs text-label-sm"
      style={{ background: c.bg, color: c.fg }}
    >
      {children}
    </span>
  );
}

function Badge({ rows, mode, accent }: { rows: EventRow[]; mode: string; accent: string }) {
  return (
    <div
      className="inline-block rounded-pill px-lg py-xs text-label-md"
      style={{ background: `color-mix(in srgb, ${accent} 12%, transparent)`, color: accent }}
    >
      {lensSourceBadge(rows, mode, RUN_WINDOW)}
    </div>
  );
}

/** 목록 화면 껍데기 — ★ 기간 칩이 **있다**. */
function ListFrame({
  title,
  subtitle,
  accent,
  render,
}: {
  title: string;
  subtitle: string;
  accent: string;
  render: (rows: EventRow[], P: ReturnType<typeof useSettings>['settings']) => React.ReactNode;
}) {
  const { rangeKey, setRangeKey } = useAppState();
  const P = useSettings().settings;
  const { rows, loading, error, mode } = useLensData(rangeKey);
  return (
    <AppShell title={title} subtitle={subtitle} accent={accent} rangeKey={rangeKey} onRangeChange={setRangeKey}>
      {loading ? (
        <p className="text-body-md text-muted-foreground">불러오는 중…</p>
      ) : error !== null ? (
        <Panel tone="error">데이터를 읽지 못했습니다 — {error}</Panel>
      ) : (
        <div className="space-y-xl">
          <Badge rows={rows} mode={mode} accent={accent} />
          {render(rows, P)}
        </div>
      )}
    </AppShell>
  );
}

/**
 * 상세 화면 껍데기 — ⚠️ 기간 칩이 **없다**.
 *
 * ★ `windowed`(회차 창까지만)를 넘긴다. 기간 필터를 거치지 않으므로 어느
 *   기간을 보고 있든 그 회차/장면이 열린다.
 */
function DetailFrame({
  title,
  subtitle,
  accent,
  render,
}: {
  title: string;
  subtitle: string;
  accent: string;
  render: (windowed: EventRow[]) => React.ReactNode;
}) {
  // ⚠️ `rangeKey` 를 'all' 로 고정한다 — 상세는 기간에 좌우되지 않는다.
  const { windowed, loading, error, mode } = useLensData('all');
  return (
    <AppShell title={title} subtitle={subtitle} accent={accent}>
      {loading ? (
        <p className="text-body-md text-muted-foreground">불러오는 중…</p>
      ) : error !== null ? (
        <Panel tone="error">데이터를 읽지 못했습니다 — {error}</Panel>
      ) : (
        <div className="space-y-xl">
          <Badge rows={windowed} mode={mode} accent={accent} />
          {render(windowed)}
        </div>
      )}
    </AppShell>
  );
}

// --- 회차 목록 ---------------------------------------------------------------

export function RunListPage() {
  return (
    <ListFrame
      title="회차 목록"
      accent={AXIS.runs}
      subtitle="최근 시작 순. 한 줄을 누르면 구조화 턴 타임라인으로 이동합니다."
      render={(rows, P) => {
        const runs = lensRunSummariesV2(rows, P.costPerTurnKrw, P.usdKrwRate, P.costFeeRate);
        return (
          <Panel>
            {runs.length === 0 ? (
              <p className="text-body-md text-muted-foreground">이 기간에 회차가 없습니다.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[720px] text-body-md">
                  <thead>
                    <tr className="border-b border-border text-left text-label-md text-muted-foreground">
                      <th className="py-sm font-medium">회차</th>
                      <th className="py-sm font-medium">시작</th>
                      <th className="py-sm font-medium">소요</th>
                      <th className="py-sm font-medium">턴</th>
                      <th className="py-sm font-medium">결과</th>
                      <th className="py-sm font-medium">막힘</th>
                      <th className="py-sm text-right font-medium">원가</th>
                    </tr>
                  </thead>
                  <tbody>
                    {runs.map((r) => (
                      <tr key={r.runId} className="border-b border-border last:border-0 hover:bg-[var(--lens-bg)]">
                        <td className="py-sm">
                          <Link
                            to={`/runDetail/${encodeURIComponent(r.runId)}`}
                            className="font-mono text-label-md text-primary underline-offset-2 hover:underline"
                          >
                            {r.runId}
                          </Link>
                        </td>
                        <td className="py-sm tabular-nums">{r.startedAtText}</td>
                        <td className="py-sm tabular-nums">{r.durationText}</td>
                        <td className="py-sm tabular-nums">{r.turnCountText}</td>
                        <td className="py-sm"><Chip tone={r.tone}>{r.endingText}</Chip></td>
                        <td className="py-sm tabular-nums">{r.blockedText}</td>
                        <td className="py-sm text-right tabular-nums">{r.costText}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        );
      }}
    />
  );
}

// --- 회차 상세 ---------------------------------------------------------------

/**
 * ★★ **「문제 턴만」이 기본이다.** 25~31줄을 다 훑어야 사고를 찾는 것은
 * 도구가 아니다.
 *
 * ⚠️ 세 가지가 중요하다 — ① 걸러낸 건수를 **요약줄로 반드시 밝힌다**(안 밝히면
 * 사고가 없었던 것인지 화면이 숨긴 것인지 구분할 수 없다) · ② 번호는 **거르기
 * 전 기준**이다(다시 매기면 「25줄 중 11번째」라는 정보가 사라진다) ·
 * ③ 걸린 이유를 줄 안에 쓴다.
 */
export function RunDetailPage() {
  const { runId = '' } = useParams();
  const [mode, setMode] = useState<'problems' | 'all'>('problems');
  return (
    <DetailFrame
      title="회차 상세"
      accent={AXIS.runs}
      subtitle="구조화 턴 타임라인."
      render={(windowed) => {
        const rows = lensTurnTimelineV2(windowed, runId, mode);
        return (
          <>
            <Panel title="턴 타임라인" note="동사 · 장면 · 판정 결과 · 부수효과만 표시합니다.">
              {/* ⚠️ 요약줄이 **걸러낸 건수를 밝힌다.** */}
              <p className="mb-md text-body-md">{lensTurnProblemSummary(windowed, runId)}</p>
              <div className="mb-lg flex gap-sm">
                {(['problems', 'all'] as const).map((m) => (
                  <button
                    key={m}
                    onClick={() => setMode(m)}
                    aria-pressed={mode === m}
                    className={
                      'rounded-pill border px-md py-xs text-label-md ' +
                      (mode === m
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border bg-card text-muted-foreground')
                    }
                  >
                    {m === 'problems' ? '문제 턴만' : '전체'}
                  </button>
                ))}
              </div>
              {rows.length === 0 ? (
                <p className="text-body-md text-muted-foreground">보여줄 줄이 없습니다.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[720px] text-body-md">
                    <thead>
                      <tr className="border-b border-border text-left text-label-md text-muted-foreground">
                        <th className="py-sm font-medium">#</th>
                        <th className="py-sm font-medium">시각</th>
                        <th className="py-sm font-medium">이벤트</th>
                        <th className="py-sm font-medium">장면</th>
                        <th className="py-sm font-medium">동사</th>
                        <th className="py-sm font-medium">상세</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((t) => (
                        <tr key={t.seq} className="border-b border-border last:border-0">
                          {/* ★ 번호는 **거르기 전 기준**이다. */}
                          <td className="py-sm tabular-nums text-muted-foreground">{t.seq}</td>
                          <td className="py-sm tabular-nums">{t.timeText}</td>
                          <td className="py-sm"><Chip tone={t.tone}>{t.eventLabel}</Chip></td>
                          <td className="py-sm font-mono text-label-md">{t.sceneId}</td>
                          <td className="py-sm">{t.verb}</td>
                          <td className="py-sm text-muted-foreground">{t.detail}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Panel>
            <PrivacyNotice />
          </>
        );
      }}
    />
  );
}

// --- 장면 목록 ---------------------------------------------------------------

export function SceneListPage() {
  return (
    <ListFrame
      title="장면 목록"
      accent={AXIS.scenes}
      subtitle="장면 단위 진입·완료·이탈률."
      render={(rows) => (
        <>
          <Notice text={lensFunnelNotice(rows)} />
          <FunnelSteps
            color={AXIS.scenes}
            title="장면 퍼널"
            note="막대 길이는 첫 장면 대비입니다 — 각 줄을 100% 로 맞추면 절대량이 줄어드는 모양을 못 봅니다. 가장 크게 빠진 칸만 빨강입니다."
            items={lensSceneFunnel(rows)}
          />

          <Panel title="장면 목록">
            <SceneTable rows={rows} />
          </Panel>

          <Notice text={lensFlagNotice(rows, FLAG_FLOOR_PCT)} />
          <RateBars
            color={AXIS.scenes}
            floorPct={FLAG_FLOOR_PCT}
            title="장면별 플래그 획득률"
            note="분모는 그 장면에 진입한 회차입니다 — 턴 수로 나누면 오래 머문 장면이 불리해져 비교가 안 됩니다. 표본 20회차 미만은 비율이 흔들려 배너가 판단하지 않습니다."
            items={lensFlagRates(rows)}
          />
        </>
      )}
    />
  );
}

function SceneTable({ rows }: { rows: EventRow[] }) {
  const scenes = lensSceneStats(rows);
  if (scenes.length === 0) {
    return <p className="text-body-md text-muted-foreground">이 기간에 장면 기록이 없습니다.</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[720px] text-body-md">
        <thead>
          <tr className="border-b border-border text-left text-label-md text-muted-foreground">
            <th className="py-sm font-medium">장면</th>
            <th className="py-sm font-medium">챕터</th>
            <th className="py-sm font-medium">진입</th>
            <th className="py-sm font-medium">완료</th>
            <th className="py-sm font-medium">이탈</th>
            <th className="py-sm font-medium">완료율</th>
            <th className="py-sm font-medium">평균 턴</th>
          </tr>
        </thead>
        <tbody>
          {scenes.map((s) => (
            <tr key={s.sceneId} className="border-b border-border last:border-0 hover:bg-[var(--lens-bg)]">
              <td className="py-sm">
                <Link
                  to={`/sceneDetail/${encodeURIComponent(s.sceneId)}`}
                  className="font-mono text-label-md text-primary underline-offset-2 hover:underline"
                >
                  {s.sceneId}
                </Link>
              </td>
              <td className="py-sm font-mono text-label-md">{s.chapterId}</td>
              <td className="py-sm tabular-nums">{s.enteredText}</td>
              <td className="py-sm tabular-nums">{s.completedText}</td>
              <td className="py-sm tabular-nums">{s.dropText}</td>
              <td className="py-sm"><Chip tone={s.tone}>{s.completionPctText}</Chip></td>
              <td className="py-sm tabular-nums">{s.avgTurnsText}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// --- 장면 상세 ---------------------------------------------------------------

export function SceneDetailPage() {
  const { sceneId = '' } = useParams();
  const { soroBaseUrl } = useAppState();
  return (
    <DetailFrame
      title="장면 상세"
      accent={AXIS.scenes}
      subtitle={sceneId}
      render={(windowed) => (
        <>
          <RankBars
            color={AXIS.scenes}
            title="동사별 시도 분포"
            note="lensVerbDist 와 같은 규칙으로 셉니다(거부된 시도 포함) — 어긋나면 전체 분포와 장면 분포의 합이 맞지 않습니다."
            items={lensSceneVerbDist(windowed, sceneId)}
          />
          <SoroPanel
            url={lensSoroSceneUrl(soroBaseUrl, 'campaign_01', '1.0.3', sceneId)}
          />
        </>
      )}
    />
  );
}

/**
 * SORO 연동.
 *
 * ★★ **문제를 발견하는 것까지가 LENS 의 역할이다.** 콘텐츠 수정은 SORO 에서
 * Validator 를 거쳐 수행한다 — LENS 는 읽기 전용이다.
 */
function SoroPanel({ url }: { url: string }) {
  return (
    <Panel title="SORO 연동">
      <p className="mb-md text-body-md text-muted-foreground">
        문제를 발견하는 것까지가 LENS의 역할입니다. 콘텐츠 수정은 SORO에서 Validator를 거쳐
        수행합니다.
      </p>
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="inline-block rounded-md border border-border bg-card px-lg py-sm text-title-sm text-primary"
      >
        SORO에서 이 장면 열기
      </a>
      <p className="mt-lg text-body-sm text-muted-foreground">
        <span className="text-title-sm text-foreground">콘텐츠 파이프라인</span>
        <br />
        Phase 2 · 준비 중 — SORO가 개발되면 상태 표시가 들어갑니다. LENS는 읽기 전용이므로 상태
        전이는 SORO에서 합니다.
      </p>
    </Panel>
  );
}

/**
 * 개인정보 고지.
 *
 * ★★★ **절대 원칙이다** — 플레이어 입력 원문과 AI 서술 원문을 저장·표시하지
 * 않는다. 이벤트 스키마에 그 필드가 **존재하지 않는 방식**으로 강제한다.
 */
function PrivacyNotice() {
  return (
    <Panel>
      <p className="text-body-sm text-muted-foreground">
        LENS는 플레이어 입력 원문과 AI 서술 원문을 저장하거나 표시하지 않습니다. 이벤트 스키마에
        해당 필드가 존재하지 않습니다. 원문 조사가 필요하면 서버 측 별도 도구에서 접근 권한을
        분리해 처리합니다.
      </p>
    </Panel>
  );
}
